import Logo from '@assets/images/common/brand/logo-primary.svg';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useChatEditing } from '@src/context/ChatEditingContext';
import { useIsModal } from '@src/context/IsModalContext';
import { MobileSidebarContext } from '@src/context/MobileSidebarContext';
import { useUnsavedInviteUsers } from '@src/context/UnsavedInviteUsersContext';
import { ConfirmMemberInvitationLeaveModal } from '@src/features/shared/community/components/ConfirmMemberInvitationLeaveModal';
import useIsMobile from '@src/hooks/useIsMobile';
import { COMPANY_URL } from '@src/routes/company';
import { SHARED_URL } from '@src/routes/shared';
import { TENANT_URL } from '@src/routes/tenant';
import { loadChannels, resetChannelCounts } from '@src/store/channelSlice';
import { AppDispatch, RootState } from '@src/store/store.ts';
import ConfirmLeaveModal from '@src/ui/templates/Shared/ConfirmLeaveModal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import ChannelItem from './CommunityChannelItem';

interface Props {
  handleCloseClick?: () => void;
  currentChannelCode?: string;
  isTenant?: boolean;
}

const CommunitySidebar: React.FC<Props> = ({ handleCloseClick, currentChannelCode, isTenant }) => {
  const dispatch = useDispatch<AppDispatch>();
  const channels = useSelector((state: RootState) => state.channel.channels);

  const navigate = useNavigate();
  const location = useLocation();
  const { unsavedInviteUsers, setUnsavedInviteUsers } = useUnsavedInviteUsers();
  const {
    isEditingLeaveModalOpen,
    editingChatId,
    openEditingLeaveModal,
    confirmEditingLeaveModal,
    closeEditingLeaveModal,
  } = useChatEditing();
  const [isInvitationLeaveModalOpen, setIsInvitationLeaveModalOpen] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const { toggle: toggleMobileSidebar } = useContext(MobileSidebarContext);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const isFileDownloading = useSelector((state: RootState) => state.fileDownload.isFileDownloading);
  const { isAnyModalOpen } = useIsModal();
  const { profile } = useSharedAuth();

  const badgeBaseClasses = useMemo<string>(
    () =>
      'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[6px] text-xs font-medium leading-none text-gray-600',
    [],
  );
  const badgeCustomStyle = useMemo<React.CSSProperties>(() => ({ backgroundColor: '#d9d9d9' }), []);

  useEffect(() => {
    if (isAnyModalOpen || isFileDownloading) {
      return;
    }

    const focusTimeoutId = setTimeout(() => {
      if (!isAnyModalOpen && !isFileDownloading) {
        dispatch(loadChannels());
      }
    }, 300);

    const timer = setInterval(() => {
      if (!isAnyModalOpen && !isFileDownloading) {
        dispatch(loadChannels());
      }
    }, 60_000);

    return () => {
      clearTimeout(focusTimeoutId);
      clearInterval(timer);
    };
  }, [dispatch, isAnyModalOpen, isFileDownloading]);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, to: string, channelCode?: string) => {
      if (isMobile) {
        toggleMobileSidebar();
      }

      if (channelCode && channelCode !== currentChannelCode) {
        dispatch(resetChannelCounts(channelCode));
      }

      // チャンネル招待画面からの遷移時に未招待ユーザーがいるかチェック
      if (unsavedInviteUsers.length > 0) {
        e.preventDefault();
        setPendingNavigation(to);
        setIsInvitationLeaveModalOpen(true);
        return;
      }

      // チャット画面からの遷移時に編集中かチェック
      if (editingChatId !== null) {
        e.preventDefault();
        openEditingLeaveModal(to);
        return;
      }

      navigate(to);
    },
    [
      isMobile,
      toggleMobileSidebar,
      currentChannelCode,
      unsavedInviteUsers.length,
      editingChatId,
      navigate,
      openEditingLeaveModal,
      dispatch,
    ],
  );

  const homeUrl = useMemo<string>(
    () => (isTenant ? TENANT_URL.HOME : COMPANY_URL.HOME),
    [isTenant],
  );

  const handleHomeClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      handleLinkClick(e, homeUrl);
    },
    [handleLinkClick, homeUrl],
  );

  const handleConfirmInvitationLeave = useCallback(() => {
    setIsInvitationLeaveModalOpen(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setUnsavedInviteUsers([]);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate, setUnsavedInviteUsers]);

  const handleCancelInvitationLeave = useCallback(() => {
    setPendingNavigation(null);
    setIsInvitationLeaveModalOpen(false);
  }, []);

  const renderedChannels =
    channels && channels.length > 0
      ? channels.map((channel) => (
          <ChannelItem
            key={`${channel.code}-${channel.unread_count}-${channel.has_unread}`}
            channel={channel}
            isCurrent={currentChannelCode === channel.code}
            handleLinkClick={handleLinkClick}
            badgeBaseClasses={badgeBaseClasses}
            badgeCustomStyle={badgeCustomStyle}
          />
        ))
      : null;

  const shouldShowChannels = Boolean(channels?.length > 0);
  const isAdmin = profile?.isTenant && profile?.tenantProfile?.user?.isAdmin;
  const isChannelListActive = location.pathname === SHARED_URL.COMMUNITY_CHANNEL_LIST;

  const handleChannelListClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleLinkClick(e, SHARED_URL.COMMUNITY_CHANNEL_LIST);
    },
    [handleLinkClick],
  );

  return (
    <aside className='min-h-dvh overflow-y-auto border-r border-solid border-r-gray-200 bg-white lg:border-r-0'>
      <div className='relative lg:border-b lg:border-solid lg:border-b-gray-200'>
        <Link
          to={homeUrl}
          className='block p-[10px]'
          onClick={handleHomeClick}
        >
          <img
            src={Logo}
            alt=''
            className='block size-full max-h-[40px] object-contain'
          />
        </Link>
        <button
          className='absolute left-0 top-0 z-50 hidden size-[60px] items-center justify-center lg:flex'
          aria-expanded='true'
          aria-label='閉じる'
          onClick={handleCloseClick}
        >
          <span className='absolute inset-0 m-auto size-[4px] h-[2px] w-[18px] rotate-45 rounded-md bg-primary'></span>
          <span className='absolute inset-0 m-auto size-[4px] h-[2px] w-[18px] -rotate-45 rounded-md bg-primary'></span>
        </button>
      </div>
      <div className='flex flex-col gap-4 px-3 py-[8px] text-sm md:p-6'>
        <div>
          <ul className='flex flex-col gap-2 px-2 pb-[80px] pt-2 md:pt-1'>
            {isAdmin && (
              <li>
                <div
                  className={`flex w-full cursor-pointer items-center pb-2 ${
                    isChannelListActive ? 'border-b-2 border-primary-500' : ''
                  }`}
                  onClick={handleChannelListClick}
                >
                  <div
                    className={`flex flex-1 items-center gap-2 truncate pb-2 ${
                      isChannelListActive
                        ? 'font-bold text-primary-500'
                        : 'font-normal text-gray-700'
                    }`}
                  >
                    <span>#</span>
                    <div className='w-fit flex-1 truncate text-sm'>すべてのチャンネル</div>
                  </div>
                </div>
              </li>
            )}
            {shouldShowChannels && renderedChannels}
          </ul>
        </div>
      </div>
      <ConfirmMemberInvitationLeaveModal
        isOpen={isInvitationLeaveModalOpen}
        onConfirm={handleConfirmInvitationLeave}
        onClose={handleCancelInvitationLeave}
      />
      <ConfirmLeaveModal
        isOpen={isEditingLeaveModalOpen}
        onConfirm={confirmEditingLeaveModal}
        onClose={closeEditingLeaveModal}
      />
    </aside>
  );
};

export default CommunitySidebar;
